import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Box, CircularProgress, Typography } from '@mui/material';
import { PictureAsPdf } from '@mui/icons-material';

// Lazy loading pages
const Home = React.lazy(() => import('./pages/Home'));
const Login = React.lazy(() => import('./pages/Login'));
const Signup = React.lazy(() => import('./pages/Signup'));
const Admin = React.lazy(() => import('./pages/Admin'));
const Profile = React.lazy(() => import('./pages/Profile'));
const ImageConverter = React.lazy(() => import('./pages/ImageConverter'));
const PDFClip = React.lazy(() => import('./pages/PDFClip'));
const TrafficControl = React.lazy(() => import('./pages/TrafficControl'));
const Mobile = React.lazy(() => import('./pages/Mobile'));
const ArticleTop = React.lazy(() => import('./pages/ArticleTop'));
const ArticleDetail = React.lazy(() => import('./pages/ArticleDetail'));
const PDFAnnotation = React.lazy(() => import('./pages/PDFAnnotation'));

// Loading Fallback Component
const LoadingFallback = () => (
  <Box sx={{
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    backdropFilter: 'blur(10px)',
  }}>
    <Box sx={{
      width: 80,
      height: 80,
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      mb: 3,
      boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
      animation: 'pulse 2s infinite',
      '@keyframes pulse': {
        '0%': {
          transform: 'scale(1)',
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
        },
        '50%': {
          transform: 'scale(1.05)',
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.5)',
        },
        '100%': {
          transform: 'scale(1)',
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
        },
      },
    }}>
      <PictureAsPdf sx={{ color: 'white', fontSize: 40 }} />
    </Box>
    <Typography variant="h5" sx={{
      fontWeight: 600,
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      mb: 1,
    }}>
      セツビズフォト
    </Typography>
    <Typography variant="body1" sx={{
      color: '#64748b',
      textAlign: 'center',
      maxWidth: 300,
    }}>
      読み込み中...
    </Typography>
  </Box>
);

function App() {
  return (
    <Router>
      <AuthProvider>
        <LanguageProvider>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/image-converter" element={<ImageConverter />} />
              <Route path="/pdf-clip" element={<PDFClip />} />
              <Route path="/traffic-control" element={<TrafficControl />} />
              <Route path="/mobile" element={<Mobile />} />
              <Route path="/articles" element={<ArticleTop />} />
              <Route path="/articles/:id" element={<ArticleDetail />} />
              <Route path="/pdf-annotation" element={<PDFAnnotation />} />
              <Route path="/" element={<Home />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </LanguageProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
